import { injectMediaAPI } from './api';
import bindApi from './api/private/bind';
import bindController from './controller/private/bind';
import bindDB from './db/private/bind';
import { IConfigCacheAssets, IConfigCacheName, IContainer, injectConfigCache } from './interface';
import bindJobs from './jobs/private/bind';
import bindModel from './model/private/bind';
import privateContainer from './private/container';
import bindSession from './session/private/bind';
import { IMediaURLResolverParameters, injectMediaCacheController } from './util/interface';
import bindUtil from './util/private/bind';

export interface IBinder {
    bind: (container: IContainer) => void;
    unbind: (container: IContainer) => void;
    expose: (srcContainer: IContainer, dstContainer: IContainer) => void;
}

/* list of binders sorted in order of installation */
const binders: Array<{ binder: IBinder; isPrivate?: boolean }> = [
    {
        binder: bindModel,
    },
    {
        isPrivate: true,
        binder: bindUtil,
    },
    {
        isPrivate: true,
        binder: bindApi,
    },
    {
        isPrivate: true,
        binder: bindDB,
    },
    {
        isPrivate: true,
        binder: bindController,
    },
    {
        binder: bindJobs,
    },
    {
        binder: bindSession,
    },
];

export function bindAll(container: IContainer) {
    binders.forEach(binder => binder.binder.bind(container));
}

function _postPrivateBindSetup(privateContainer: IContainer) {
    /* Add ats-workspace.com cache resolver to MediaController. 
       Late bind all uses of MediaAPI, as the size of the array may change at runtime, pull the latest definition on use */
    const mediaAPI = () => injectMediaAPI(privateContainer);
    const cacheController = injectMediaCacheController(privateContainer);

    const config = injectConfigCache(privateContainer);
    const configCache = config?.[IConfigCacheName.WorkspaceMedia] as IConfigCacheAssets | undefined;
    const apiCacheName = configCache?.cacheName ?? 'cache-apiMedia';
    const apiResolver = (mediaId: string, parameters?: IMediaURLResolverParameters) =>
        mediaAPI()
            .map(api => api.requestURL(mediaId))
            .flat(1);
    if (apiCacheName) {
        cacheController.addCacheResolver(apiCacheName, apiResolver);
    }
}

function setup(containerGlobal: IContainer) {
    const prvContainer = privateContainer(containerGlobal);

    /* bind all to private container */
    bindAll(prvContainer);

    /* expose (pre-bound instantiated values/objects) to parent global container from private
       container. This is to ensure both containers use the same object ref (if injectable is 
       an instantiated value). */
    binders
        .filter(binder => !binder.isPrivate)
        .forEach(binder => binder.binder.expose(prvContainer, containerGlobal));

    _postPrivateBindSetup(prvContainer);
}

export default setup;
